import { EVENT } from "@/constants/event";
import imageUrl, { extractBucketFromUrl } from "@/utils/imageUrl";
import classNames from "classnames";
import { noop } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import HBIcon from "../HBIcon";
import styles from "./index.module.less";
import LoadErrorImage, { exampleFile } from "./LoadErrorImage";

const customLoader = ({ src, width }) => {
  if (width) {
    return `${src}`;
  }
  return src;
};

// 可以在文件顶部添加一个全局的防抖检测
let pendingCheckPromise = null;
let pendingCheckTime = 0;

const IMG_LOAD_PENDING_TIMEOUT = 30000;
const CHECK_PENDING_TIMEOUT = 5000;

/**
 * 检测图片是否可以正常加载，而非特例的图片加载超时
 * @returns {Promise<boolean>}
 */
function checkImagePendingTimeout(imgSrc) {
  const now = Date.now();
  // 3秒内复用上一次的检测结果
  if (pendingCheckPromise && now - pendingCheckTime < 3000) {
    return pendingCheckPromise;
  }

  // 从imgSrc中提取bucket
  const bucket = extractBucketFromUrl(imgSrc) || exampleFile.bucket;

  const exampleUrl = imageUrl(exampleFile, {
    width: 75,
    rect: "sq",
    bucket,
  });

  pendingCheckTime = now;
  pendingCheckPromise = new Promise((resolve) => {
    const image = new Image();

    let loaded = false;
    image.onload = () => {
      loaded = true;
      // image load is ok
      resolve(false);
    };
    image.onerror = () => {
      loaded = true;
      // image load is ok
      resolve(false);
    };

    image.src = `${exampleUrl}?time=${Date.now()}`;

    setTimeout(() => {
      if (!loaded) {
        // image load is pending
        resolve(true);
      }
    }, CHECK_PENDING_TIMEOUT);
  });

  return pendingCheckPromise;
}

/**
 *
 * @param {{
 *  src: string;
 *  preSrc?: string;
 *  srcset?: string;
 *  srcSet?: string;
 *  width?: number;
 *  height?: number;
 *  alt?: string;
 *  title?: string;
 *  style?: React.CSSProperties;
 *  className?: string;
 *  preClassName?: string;
 *  loading?: "eager" | "lazy";
 *  loader?: (props: { src: string; width: number }) => string;
 *  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
 *  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
 *  showError?: boolean;
 *  alternate?: string,
 * } & import("react").Attributes<HTMLImageElement>} param0
 * @returns
 */
export default function HBImage({
  src,
  preSrc = "",
  srcset = "",
  srcSet = "",
  width,
  height,
  alt,
  title = "",
  style = {},
  className,
  preClassName = "",
  loading = "lazy", // eager | lazy
  loader = customLoader,
  onError = noop,
  onLoad = noop,
  showError = false,
  limit = false,
  /**备用, 当默认图片地址访问不了时，展示此备用地址 */
  alternate,
  ...props
}) {
  const imgRef = useRef(null);
  const loadingRef = useRef(false);
  const [error, setError] = useState(false);
  const [preLoading, setPreLoading] = useState(!!preSrc);

  const onImgError = useCallback(
    (...props) => {
      if (showError) {
        setError(true);
      }
      if (alternate) {
        imgRef.current.src = alternate;
        imgRef.current.srcset = alternate;
      }
      onError && onError(...props);
    },
    [onError, showError, alternate]
  );

  const onImgLoad = useCallback(
    (...props) => {
      if (!loadingRef.current) {
        loadingRef.current = true;
        setPreLoading(false);
        onLoad && onLoad(...props);
      }
    },
    [onLoad]
  );

  src = loader({ src, width });

  // 缓存的图片不触发onLoad，所以判断一下，手动触发
  useEffect(() => {
    if (imgRef.current?.complete && !loadingRef.current) {
      loadingRef.current = true;
      setPreLoading(false);

      // 如果加载完成了，但是宽度为0，说明失败了
      if (imgRef.current.naturalWidth === 0) {
        onImgError({
          target: imgRef.current,
        });
      } else {
        onLoad &&
          onLoad({
            target: imgRef.current,
          });
      }
    }
  }, [onLoad, onImgError]);

  // refactor: 图片pending超时优化，当图片加载超时时，自动切换域名处理
  useEffect(() => {
    // loading 为 lazy 时，不进行超时检测，因为lazy的图片会在滚动到可见区域时加载，这个检测方式不适用
    if (loading === "lazy") return;
    if (!imgRef.current) {
      return;
    }

    // 超时检测
    const timeout = setTimeout(() => {
      const isLoaded = imgRef.current?.complete;

      if (!isLoaded) {
        console.warn("图片加载超时", src);
        checkImagePendingTimeout(src).then((isPending) => {
          console.warn("图片Pending超时检测结果", isPending);
          if (isPending) {
            const event = new CustomEvent(EVENT.IMAGE_LOAD_ERROR, {
              detail: { src },
            });

            window.dispatchEvent(event);
          }
        });
      }
    }, IMG_LOAD_PENDING_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
  }, [loading, src]);

  return (
    <>
      <If condition={error}>
        <Then>
          <LoadErrorImage
            src={src}
            srcset={srcset || srcSet}
            width={width}
            height={height}
            limit={limit}
          />
        </Then>
        <Else>
          <>
            {preLoading ? (
              <div className={classNames([styles.pre, preClassName])}>
                <img
                  className={classNames(className, "hb-image")}
                  style={style}
                  alt={alt || title}
                  title={title}
                  src={preSrc}
                  width={width}
                  height={height}
                  loading={preSrc ? "eager" : loading}
                />
                <div className={styles.loading}>
                  <HBIcon className={styles.icon} type="replay" />
                  <div className={styles.text}>加载中</div>
                </div>
              </div>
            ) : null}
            <img
              className={classNames(className, "hb-image")}
              ref={imgRef}
              style={preLoading ? { display: "none" } : style}
              alt={alt || title}
              title={title}
              src={src}
              srcSet={srcset || srcSet}
              width={width}
              height={height}
              loading={preSrc ? "eager" : loading}
              onError={onImgError}
              onLoad={onImgLoad}
              decoding="async"
              {...props}
            />
          </>
        </Else>
      </If>
    </>
  );
}
